(() => {
  "use strict";
  const prefix = "#asv-rechner=";
  const fieldsOf = root => [...root.querySelectorAll("input[data-feld], select[data-feld]")];

  function validValue(field, value) {
    if (typeof value !== "string" || value.length > 40) return false;
    if (field.type === "checkbox") return value === "0" || value === "1";
    if (field.tagName === "SELECT") return [...field.options].some(option => option.value === value);
    if (field.type !== "number" || value.trim() === "" || !Number.isFinite(Number(value))) return false;
    // Existing calculators accept arbitrary numbers within their bounds;
    // the step attribute controls the spinner, not the calculation precision.
    const number = Number(value);
    return (field.min === "" || number >= Number(field.min)) &&
      (field.max === "" || number <= Number(field.max));
  }

  function start() {
    document.querySelectorAll("[data-rechner-teilen]").forEach(box => {
      if (box.dataset.bereit) return;
      box.dataset.bereit = "1";
      const root = box.closest(".asv-rechner-inner")?.querySelector("[data-rechner]");
      if (!root) return;
      const fields = fieldsOf(root);
      const output = box.querySelector("[data-teilen-ausgabe]");
      const status = box.querySelector("[data-teilen-status]");
      const canonical = document.querySelector('link[rel="canonical"]')?.href;
      const base = new URL(canonical || location.pathname, location.origin);
      base.hash = "";
      base.search = "";
      const say = message => { status.textContent = message; };

      function restore() {
        if (!location.hash.startsWith(prefix)) return;
        try {
          if (location.hash.length > 4000) throw new Error("length");
          const params = new URLSearchParams(location.hash.slice(prefix.length));
          const allowed = new Set(["v", "typ", ...fields.map(field => `f.${field.dataset.feld}`)]);
          if (params.get("v") !== "1" || params.get("typ") !== root.dataset.rechner ||
              new Set(params.keys()).size !== params.size || [...params.keys()].some(key => !allowed.has(key))) throw new Error("version");
          const values = fields.map(field => {
            const value = params.get(`f.${field.dataset.feld}`);
            if (value !== null) return value;
            if (field.dataset.shareOptional === "true") {
              const id = field.dataset.feld.replace(/^te_/, "");
              const count = Number(params.get(`f.anzahl_${id}`) ?? root.querySelector(`[data-feld="anzahl_${id}"]`)?.value ?? 0);
              if (count === 0) return "";
              throw new Error("missing-dependent-value");
            }
            return field.dataset.shareDefault ?? null;
          });
          if (fields.some((field, index) => values[index] !== "" && !validValue(field, values[index]))) throw new Error("value");
          fields.forEach((field, index) => {
            if (field.type === "checkbox") field.checked = values[index] === "1";
            else field.value = values[index];
          });
          fields.forEach(field => field.dispatchEvent(new Event("change", { bubbles: true })));
          say("Geteilte Eingaben geladen. Das Ergebnis wird mit der aktuellen Rechnerfassung neu berechnet.");
        } catch {
          say("Dieser Eingabelink ist ungültig oder passt nicht zur aktuellen Rechnerfassung. Bitte die Werte neu eingeben.");
        }
      }
      restore();
      window.addEventListener("hashchange", restore);

      async function copy(value, message) {
        output.hidden = false;
        output.value = value;
        try {
          if (!navigator.clipboard?.writeText) throw new Error("clipboard");
          await navigator.clipboard.writeText(value);
          say(message);
        } catch {
          output.focus();
          output.select();
          say("Der Text ist markiert. Sie können ihn jetzt kopieren.");
        }
      }
      box.querySelector("[data-eingaben-kopieren]").addEventListener("click", () => {
        if (root.dataset.veraltet) { say("Bitte laden Sie zuerst die aktuelle Rechnerfassung neu."); return; }
        const params = new URLSearchParams({ v: "1", typ: root.dataset.rechner });
        for (const field of fields) {
          const value = field.type === "checkbox" ? (field.checked ? "1" : "0") : field.value;
          if (field.dataset.shareOptional === "true" && value === "") {
            const id = field.dataset.feld.replace(/^te_/, "");
            if (Number(root.querySelector(`[data-feld="anzahl_${id}"]`)?.value ?? 0) === 0) continue;
          }
          if (!validValue(field, value)) { field.reportValidity(); say("Bitte zuerst die markierten Eingaben prüfen."); return; }
          params.set(`f.${field.dataset.feld}`, value);
        }
        void copy(`${base.href}${prefix}${params}`, "Link mit Ihren Eingaben kopiert.");
      });
      box.querySelector("[data-quelle-kopieren]").addEventListener("click", () => {
        const title = root.closest("section")?.querySelector("h1")?.textContent?.trim() || document.title;
        const date = new Intl.DateTimeFormat("de-DE").format(new Date());
        void copy(`asvendo GmbH: ${title}. ${base.href} (abgerufen am ${date}). Orientierungshilfe; ersetzt keine fachliche Planung.`, "Quellenangabe kopiert.");
      });
      box.querySelector("[data-zusammenfassung-kopieren]")?.addEventListener("click", () => {
        if (root.dataset.veraltet) { say("Bitte laden Sie zuerst die aktuelle Rechnerfassung neu."); return; }
        const title = root.closest("section")?.querySelector("h1, h2")?.textContent?.trim() || document.title;
        const inputLines = fields.map(field => {
          const label = field.closest("label, .asv-feld")?.querySelector("span")?.textContent?.trim() || field.dataset.feld;
          const value = field.type === "checkbox" ? (field.checked ? "ja" : "nein") : field.value;
          return `${label}: ${value}`;
        });
        const result = root.querySelector("[data-ergebnis]")?.innerText?.trim() || "Noch kein Ergebnis.";
        void copy(`${title}\n\nEingaben:\n${inputLines.join("\n")}\n\nErgebnis:\n${result}\n\nOrientierungshilfe; ersetzt keine fachliche Planung.`, "Zusammenfassung kopiert.");
      });
      box.querySelector("[data-stueckliste-kopieren]")?.addEventListener("click", () => {
        const entries = fields.filter(field => field.dataset.feld.startsWith("anzahl_") && Number(field.value) > 0).map(field => {
          const id = field.dataset.feld.replace("anzahl_", "");
          const te = root.querySelector(`[data-feld="te_${id}"]`)?.value || "TE offen";
          const label = field.closest(".asv-feld--zeile")?.querySelector(":scope > span")?.textContent?.trim() || id;
          return `${field.value} × ${label} (${te} TE je Gerät)`;
        });
        if (!entries.length) { say("Für die Stückliste zuerst mindestens ein Gerät eintragen."); return; }
        void copy(`Stückliste Verteiler\n${entries.join("\n")}\n\nTE-Angaben vor Bestellung am Produktdatenblatt prüfen.`, "Stückliste kopiert.");
      });
      box.querySelector("[data-drucken]")?.addEventListener("click", () => {
        document.body.classList.add("asv-rechner-druckansicht");
        const cleanup = () => document.body.classList.remove("asv-rechner-druckansicht");
        window.addEventListener("afterprint", cleanup, { once: true });
        window.print();
      });
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
  document.addEventListener("shopify:section:load", start);
})();
